import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, message, GlobalToken } from 'antd';
import moment from 'moment';
import { cloneDeep, pick, isEqual } from 'lodash';

import { EPInput, EPTextArea, NavBack, SeverityIcon } from '@/comp';
import { AlertLevels } from '@/constants/enums';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import { EPButton, EPSelect, EPSelectOption } from '@/components';
import { Rule } from './CreateOrEditCustomRule';

const GUTTER = 24;

const initialFormState = {
  title: '',
  detailedDescription: '',
  severity: '',
};

const CreateOrEditManufacturerRule = ({ rule, setRule }: { rule: Rule; setRule: (rule: Rule) => void }) => {
  const { rulesStore, permissionStore } = useStores();
  const { stationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [formState, setFormState] = useState(cloneDeep(initialFormState));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const backUrl = location.state?.from ? location.state?.from : `/home/station/${stationId}/rules/manufacturer`;

  const isFormValid = () => {
    return !!formState.severity;
  };

  const isFormDisabled = () => {
    return isSubmitting || isReverting || !permissionStore.isAuth(permissions.UPDATE_MANUFACTURER_ALERT);
  };

  const onChangeTitle = e =>
    setFormState({
      ...formState,
      title: e.target.value,
    });

  const onChangeDescription = e =>
    setFormState({
      ...formState,
      detailedDescription: e.target.value,
    });

  const onChangeSeverity = value =>
    setFormState({
      ...formState,
      severity: value,
    });

  const onSubmit = async () => {
    const payload = {
      name: formState.title.trim(),
      longDescription: formState.detailedDescription.trim(),
      severity: formState.severity,
    };

    try {
      setIsSubmitting(true);
      await rulesStore.update(payload, 'manufacturer', rule.id);
      message.success(t('Alert rule updated successfully'));

      setTimeout(() => navigate(backUrl), 0);
    } catch (e) {
      message.error('Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRevert = async () => {
    try {
      setIsReverting(true);
      const { data } = await rulesStore.revert(rule.id);
      setRule(data);
      message.success(t('Alert rule updated successfully'));

      setTimeout(() => navigate(backUrl), 0);
    } catch (e) {
      message.error('Something went wrong');
    } finally {
      setIsReverting(false);
    }
  };

  useEffect(() => {
    // init formState with edited values from rule
    const isRuleEdited = (() => {
      if (!rule.originalTemplate) return false;

      const template = pick(rule, ['title', 'detailedDescription', 'severity']);
      const originalTemplate = pick(rule.originalTemplate, ['title', 'detailedDescription', 'severity']);
      return !isEqual(template, originalTemplate);
    })();

    const newFormState = isRuleEdited
      ? {
          title: rule.title,
          detailedDescription: rule.detailedDescription,
          severity: rule.severity,
        }
      : cloneDeep({
          ...initialFormState,
          severity: rule.severity,
        });

    setFormState(newFormState);
  }, [rule]);

  return (
    <Container>
      <NavBack url={backUrl} />

      <div className="form-container">
        <h1 className="title">{t('Update manufacturer alert rule')}</h1>

        <section>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="auto">
              <div className="subtitle">{t('Alert rule original details')}</div>
            </Col>
            <Col flex="0 1 512px">
              <div className="flex justify-between align-center">
                <div className="subtitle">{t('Edit this Alert rule appearance')}</div>

                <EPButton type="link" loading={isReverting} disabled={isFormDisabled()} onClick={onRevert}>
                  {t('Restore default')}
                </EPButton>
              </div>
            </Col>
          </Row>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="1 1 300px">
              <div className="form-group">
                <label>{t('Original rule name (Title)')}</label>
                <div className="value">{rule.originalTemplate?.title || rule.title}</div>
              </div>
            </Col>
            <Col flex="0 1 512px">
              <div className="form-group">
                <label htmlFor="">{t('Given rule name (Title)')}</label>
                <EPInput
                  placeholder={t('E.g. High then usual flow causes pressure drop')}
                  value={formState.title}
                  disabled={isFormDisabled()}
                  onChange={onChangeTitle}
                />
              </div>
            </Col>
          </Row>
        </section>
        <hr />
        <section>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="1 1 300px">
              <div className="form-group">
                <label>{t('Original description')}</label>
                <div className="value">{rule.originalTemplate?.detailedDescription || rule.detailedDescription}</div>
              </div>
            </Col>
            <Col flex="0 1 512px">
              <div className="form-group">
                <label htmlFor="">{t('Given description')}</label>
                <EPTextArea
                  placeholder={t('E.g. Try do the following...')}
                  value={formState.detailedDescription}
                  disabled={isFormDisabled()}
                  onChange={onChangeDescription}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={GUTTER} className="row-item">
            <Col flex="auto">
              <div className="form-group">
                <label>{t('Alert severity')}</label>
                <div className="value flex">
                  <span style={{ marginRight: 10 }}>
                    <SeverityIcon severity={rule.originalTemplate?.severity || rule.severity} />
                  </span>
                  <span className="capitalize-first-letter">{rule.originalTemplate?.severity || rule.severity}</span>
                </div>
              </div>
            </Col>

            <Col flex="0 1 512px">
              <div className="form-group form-group-severity" style={{ width: 246 }}>
                <label className="mandatory" htmlFor="">
                  {t('Alert severity')}
                </label>
                <EPSelect
                  value={formState.severity}
                  getPopupContainer={() => document.querySelector('.form-group.form-group-severity')}
                  disabled={isFormDisabled()}
                  onChange={onChangeSeverity}
                >
                  <EPSelectOption value="shutdown">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Shutdown} />
                      &nbsp;<span style={{ paddingTop: '0.1em' }}>{t('Shutdown')}</span>
                    </div>
                  </EPSelectOption>
                  <EPSelectOption value="warning">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Warning} />
                      &nbsp;<span style={{ paddingTop: '0.1em' }}>{t('Warning')}</span>
                    </div>
                  </EPSelectOption>
                  <EPSelectOption value="service">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Service} />
                      &nbsp;<span style={{ paddingTop: '0.1em' }}>{t('Service')}</span>
                    </div>
                  </EPSelectOption>
                </EPSelect>
              </div>
            </Col>
          </Row>
        </section>
        <hr />
        <section>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="1 1 300px">
              <div className="form-group condensed">
                <label>{t('Device name')}</label>
                <div className="value">{rule.deviceName}</div>
              </div>
            </Col>
          </Row>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="1 1 300px">
              <div className="form-group condensed">
                <label>{t('Alert rule ID')}</label>
                <div className="value">{rule.id}</div>
              </div>
            </Col>
          </Row>
          <Row gutter={GUTTER} className="row-item">
            <Col flex="1 1 300px">
              <div className="form-group condensed">
                <label>{t('Creation date')}</label>
                <div className="value">{moment(rule.createdDate).format('MMMM D, YYYY')}</div>
              </div>
            </Col>
          </Row>
        </section>
        <hr />
        <EPButton
          className="submit-btn"
          type="primary"
          disabled={!isFormValid() || isFormDisabled()}
          loading={isSubmitting}
          onClick={onSubmit}
        >
          {t('Update')}
        </EPButton>
        <p className="contact-us">
          {t('Contact us if you have any questions at:')} <a href="mailto:support@ecoplant.co">support@ecoplant.co</a>
        </p>
      </div>
    </Container>
  );
};

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  flex-grow: 1;
  padding: ${({ theme: { token } }) => token.paddingXL}px;
  overflow: scroll;

  .form-container {
    min-width: 1200px;
    margin-top: ${({ theme: { token } }) => token.marginXL}px;

    h1.title {
      font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      color: ${({ theme: { token } }) => token.colorText};
    }

    .subtitle {
      margin-top: ${({ theme: { token } }) => token.marginSM}px;
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    section {
      width: 1200px;
      padding-bottom: ${({ theme: { token } }) => token.paddingXL}px;

      &:last-child {
        border-bottom-width: 0;
      }

      .row-item {
        padding-top: ${({ theme: { token } }) => token.paddingXL}px;
        padding-right: ${({ theme: { token } }) => token.paddingXL}px;
      }

      .form-group {
        position: relative;

        label {
          display: block;
          margin-bottom: ${({ theme: { token } }) => token.marginSM}px;
          font-size: ${({ theme: { token } }) => token.fontSize}px;
          color: ${({ theme: { token } }) => token.colorTextTertiary};

          &.mandatory:before {
            content: '* ';
            color: ${({ theme: { token } }) => token.colorError};
          }
        }

        .value {
          margin-top: ${({ theme: { token } }) => token.marginLG}px;
        }

        .ant-select {
          width: 100%;
        }

        &.condensed {
          .value {
            margin-top: ${({ theme: { token } }) => token.marginSM}px;
          }
        }
      }
    }

    .submit-btn {
      margin-top: ${({ theme: { token } }) => token.marginLG}px;
    }

    p.contact-us {
      margin-top: ${({ theme: { token } }) => token.marginLG}px;
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextTertiary};
    }

    hr {
      height: 1px;
    }
  }

  hr {
    height: 2px;
  }
`;

export default CreateOrEditManufacturerRule;
